/**
 * Choisir sa photo — un selfie au téléphone, ou un fichier. L'image est réduite
 * dans le navigateur (320 px, JPEG) avant d'être envoyée en data URI : pas de
 * stockage de fichiers à opérer, un profil qui reste léger dans les listes.
 *
 * Un seul geste possible à la fois, et l'aperçu tout de suite : on voit ce que
 * les autres verront.
 */
import { useRef, useState } from 'react';
import { Camera, ImagePlus, Trash2 } from 'lucide-react';

const TAILLE = 320;

async function reduire(fichier: File): Promise<string> {
  const image = await new Promise<HTMLImageElement>((resoudre, rejeter) => {
    const url = URL.createObjectURL(fichier);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resoudre(img);
    };
    img.onerror = () => rejeter(new Error('Image illisible'));
    img.src = url;
  });
  const cote = Math.min(image.width, image.height);
  const sx = (image.width - cote) / 2;
  const sy = (image.height - cote) / 2;
  const toile = document.createElement('canvas');
  toile.width = TAILLE;
  toile.height = TAILLE;
  const ctx = toile.getContext('2d');
  if (!ctx) throw new Error('Canvas indisponible');
  ctx.drawImage(image, sx, sy, cote, cote, 0, 0, TAILLE, TAILLE);
  return toile.toDataURL('image/jpeg', 0.82);
}

export function ChoixPhoto({
  valeur,
  onChange,
  nom,
  grand = false,
}: {
  valeur: string | null;
  onChange: (dataUrl: string | null) => void;
  nom?: string;
  grand?: boolean;
}) {
  const fichierRef = useRef<HTMLInputElement>(null);
  const selfieRef = useRef<HTMLInputElement>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = async (f: File | undefined) => {
    if (!f) return;
    setErreur(null);
    try {
      onChange(await reduire(f));
    } catch {
      setErreur('Impossible de lire cette image — essayez une autre.');
    }
  };

  const taille = grand ? 'h-40 w-40' : 'h-20 w-20';
  const initiales = (nom ?? '').split(/\s+/).map((m) => m[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className={`flex ${grand ? 'flex-col items-center text-center' : 'items-center'} gap-5`}>
      {valeur ? (
        <img src={valeur} alt="" className={`${taille} rounded-full object-cover`} />
      ) : (
        <span className={`${taille} flex items-center justify-center rounded-full border border-graphite font-serif ${grand ? 'text-[40px]' : 'text-[22px]'} text-fumee`}>
          {initiales || '·'}
        </span>
      )}
      <div className={`flex flex-wrap ${grand ? 'justify-center' : ''} gap-2`}>
        <button type="button" onClick={() => selfieRef.current?.click()} className="btn-creme !min-h-0 px-4 py-2 text-[14px]">
          <Camera size={14} strokeWidth={1.5} className="mr-1.5 inline" />
          {valeur ? 'Reprendre un selfie' : 'Prendre un selfie'}
        </button>
        <button type="button" onClick={() => fichierRef.current?.click()} className="rounded-full border border-graphite px-4 py-2 text-[14px] text-fumee hover:text-blanc">
          <ImagePlus size={14} strokeWidth={1.5} className="mr-1.5 inline" />
          Choisir une image
        </button>
        {valeur && (
          <button type="button" onClick={() => onChange(null)} aria-label="Retirer la photo" className="rounded-full border border-graphite px-3 py-2 text-fumee hover:text-blanc">
            <Trash2 size={14} strokeWidth={1.5} />
          </button>
        )}
        <input ref={selfieRef} type="file" accept="image/*" capture="user" hidden onChange={(e) => void charger(e.target.files?.[0])} />
        <input ref={fichierRef} type="file" accept="image/*" hidden onChange={(e) => void charger(e.target.files?.[0])} />
      </div>
      {erreur && <p className="text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
    </div>
  );
}
